import type { UserStatusModel } from "@payping/api";
import { Button, Divider, Flex, LegacyCard, Space, Typography, usePaypingContext } from "@payping/web-components";
import { formatIBAN, useAppTranslation } from "@/utils";
import { BankKhavarMianeh, CheckCircle, FiAlertTriangle } from "@payping/icons";
import { Link } from "react-router-dom";
import { DashboardRoutes } from "@/router";

type Props = {
  status?: UserStatusModel;
};

type StatusType = "notFound" | "active" | "inProgress";

const KhavarmianehCard = (props: Props) => {
  const { breakpoints } = usePaypingContext();
  const { sm } = breakpoints;
  const { t } = useAppTranslation();
  const { status } = props;

  const accountStatus = ((): StatusType => {
    switch (status?.status) {
      case "notFound":
        return "notFound";
      case "active":
        return "active";
      case "inProgress":
        return "inProgress";
      default:
        return "notFound";
    }
  })();

  const Icon = (iconProps: { status?: StatusType }) => {
    const commonProps = {
      width: 20,
      strokeWidth: 1.5,
    };

    switch (iconProps.status) {
      case "notFound":
        return <FiAlertTriangle className="color--warning" {...commonProps} />;
      case "active":
        return <CheckCircle className="color--success" {...commonProps} />;
      case "inProgress":
        return <CheckCircle className="color--warning" {...commonProps} />;
      default:
        return null;
    }
  };

  return (
    <LegacyCard.Ant
      cover={
        <div className={sm ? "background--gray100 flex justify-center p-2" : ""}>
          <div
            className="tw:relative tw:h-49.75"
            style={{
              background: "#99540F",
              ...(sm ? { width: "370px", borderRadius: "4px" } : {}),
              ...(accountStatus !== "active"
                ? {
                    background:
                      "linear-gradient(rgba(162, 91, 219, 1),rgba(104, 59, 142, 1),rgba(104, 59, 142, 1),rgba(206, 119, 174, 1))",
                    mixBlendMode: "luminosity",
                  }
                : {}),
            }}
          >
            <div className={"tw:flex! tw:flex-col! tw:pl-5! tw:pr-5! tw:pt-8!"}>
              <Space className={"tw:mb-4! tw:flex! tw:justify-between!"}>
                <Typography.Text strong className={"color--white"}>
                  {status?.firstname} {status?.lastname}
                </Typography.Text>
                <BankKhavarMianeh width={32} />
              </Space>
              <div className={"tw:flex!"}>
                <Typography.Text className={"color--white"}>{t("profile.shaba")}</Typography.Text>
              </div>
            </div>
            <div
              className="tw:absolute tw:bottom-8 tw:h-9.5 tw:w-full"
              style={{
                background: "rgba(0, 0, 0, 0.68)",
              }}
            >
              <Typography.Text
                style={!sm ? { width: 350 } : { width: "100%" }}
                className={"color--white tw:flex! tw:h-full tw:items-center! tw:justify-end! tw:pl-8!"}
              >
                <Space size={"middle"} className={"ltr"}>
                  {formatIBAN(status?.iban || "IR------------------------")
                    ?.split(" ")
                    ?.map((code, index) => (
                      <Typography.Text key={index} className={"color--white en-font"}>
                        {code}
                      </Typography.Text>
                    ))}
                </Space>
              </Typography.Text>
            </div>
          </div>
        </div>
      }
    >
      <Flex vertical gap={16}>
        <Flex gap={8}>
          {Icon({ status: accountStatus })}
          <Typography.Title level={4} className={"tw:mb-0!"}>
            {t("digitalAccount.yourAccount.khavarmianehAccount")}
          </Typography.Title>
        </Flex>
        <Divider className={"tw:my-0!"} />
        <Flex vertical gap={8}>
          <Typography.Text type={"secondary"}>
            {accountStatus === "active"
              ? t("digitalAccount.yourAccount.successKhavarmianehDescription")
              : t("digitalAccount.yourAccount.accountAccessFormKhavarmianeh")}
          </Typography.Text>
          <Typography.Text>&nbsp;</Typography.Text>
        </Flex>
      </Flex>
      <Link to={DashboardRoutes.digitalAccount.jointAccount.khavarmianeh?.url}>
        <Button className="tw:mt-4!" type="default">
          {accountStatus === "active" ? t("login") : t("digitalAccount.yourAccount.continueOpeningAccount")}
        </Button>
      </Link>
    </LegacyCard.Ant>
  );
};

export default KhavarmianehCard;
